import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMap } from "react-leaflet";
import { GeoSearchControl } from "leaflet-geosearch";
import 'leaflet-geosearch/dist/geosearch.css';
import '../styles.css';
import { searchAreaEntries, normalizeAreaSearch } from './areaSearchIndex.js';
import useTwoChipRows from './useTwoChipRows.js';
import SearchScore from './SearchScore.js';
import { track } from '../analytics/events.js';

// Kartvy's search. The leaflet-geosearch control gives the field (styled in mapStyle.css); the list under it is
// ours, in two groups with the same options in each: the matching kommuner, län, postorter and areas
// (areaSearchIndex.js) and places from Nominatim (searchProvider.js). "Gå till" flies there (an area to its
// bounds, a place to its point); "Visa bara bostäder i" shows only the listings there (Map.js) and adds a chip
// under the field: an area as itself, a place as the area it lies in (onPlaceAreaSelect). The list and
// the chips render into document.body, placed under the field: inside the map, Leaflet and the control stop
// the clicks before React sees them.

const PLACE_DELAY_MS = 300;
// Options in each group: areas first, then places (not repeating an area's name).
const MAX_AREAS = 3;

// A kommun shows as "<name> kommun" and a län as "<name> län" (the län label already carries it), the same as
// listvy's suggestions, so the several same-named rows (e.g. kommun / postort / område "Stockholm") are told
// apart by their name, not only their tag. Postorter and områden keep the plain name (their tag marks them).
const entryDisplayName = (entry) => (entry?.type === 'kommun' ? `${entry.name} kommun` : (entry?.name || ''));
const MAX_PLACES = 3;

const sameName = (a, b) => String(a || '').toLowerCase().split(',')[0].trim() === String(b || '').toLowerCase().trim();

const SearchBox = ({
    provider,
    searchLabel,
    submitLabel,
    swedish = true,
    onSearchStart,
    onSearchResult,
    onAreaGo,
    onPlaceAreaSelect,
    areaEntries = [],
    // (entry) -> { score } | null: the area score shown beside each result (SearchScore.js).
    scoreFor = null,
    // (lat, lng) -> { score } | null: an address's or street's, its area's.
    placeScoreFor = null,
    selectedAreas = [],
    onAreaSelect,
    onAreaRemove,
    onAreasClear,
}) => {
    const map = useMap();
    const controlRef = useRef(null);
    const [form, setForm] = useState(null);
    const [query, setQuery] = useState('');
    const [places, setPlaces] = useState({ query: '', results: [] });
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [rect, setRect] = useState(null);
    const chipRows = useTwoChipRows(selectedAreas.length, selectedAreas.map((area) => area.name).join('|'));
    const propsRef = useRef({});
    propsRef.current = { provider, onSearchStart, onSearchResult, onAreaGo, onPlaceAreaSelect };

    const areas = useMemo(() => searchAreaEntries(areaEntries, query).slice(0, MAX_AREAS), [areaEntries, query]);
    const placeResults = places.query === query.trim() ? places.results : null;
    // The same options under both headings; each heading does its own thing with them.
    const items = useMemo(() => {
        const options = [
            ...areas.map((entry) => ({ kind: 'area', id: entry.id, entry })),
            ...(placeResults || [])
                .filter((result) => !areas.some((entry) => sameName(result.label, entry.name)))
                .slice(0, MAX_PLACES)
                .map((result) => ({ kind: 'place', id: `place:${result.label}`, result })),
        ];
        // "Visa bara bostäder i" (show) first, so it's the top group and the default highlighted row.
        return [
            ...options.map((option) => ({ ...option, action: 'show', key: `show:${option.id}` })),
            ...options.map((option) => ({ ...option, action: 'go', key: `go:${option.id}` })),
        ];
    }, [areas, placeResults]);
    const itemsRef = useRef(items);
    itemsRef.current = items;
    const activeRef = useRef(activeIndex);
    activeRef.current = activeIndex;

    const pickPlace = useCallback((result) => {
        const control = controlRef.current;
        if (!control || !result) return;
        control.searchElement.input.value = result.label;
        setQuery(result.label);
        setOpen(false);
        propsRef.current.onSearchStart?.(result);
        control.showResult(result, { query: result.label });
        control.searchElement.input.blur();
    }, []);
    const pickArea = useCallback((entry) => {
        const control = controlRef.current;
        if (!entry) return;
        if (control) {
            control.searchElement.input.value = '';
            control.searchElement.input.blur();
        }
        setQuery('');
        setOpen(false);
        onAreaSelect?.(entry);
    }, [onAreaSelect]);
    // "Gå till" an area: fly to it, nothing else.
    const goToArea = useCallback((entry) => {
        const control = controlRef.current;
        if (!entry) return;
        if (control) {
            control.searchElement.input.value = entryDisplayName(entry);
            control.searchElement.input.blur();
        }
        setQuery(entryDisplayName(entry));
        setOpen(false);
        propsRef.current.onAreaGo?.(entry);
    }, []);
    // "Visa bara bostäder i" a place: the area it lies in.
    const showPlaceArea = useCallback((result) => {
        const control = controlRef.current;
        if (!result) return;
        if (control) {
            control.searchElement.input.value = '';
            control.searchElement.input.blur();
        }
        setQuery('');
        setOpen(false);
        propsRef.current.onPlaceAreaSelect?.(result);
    }, []);
    const pickItem = useCallback((item) => {
        if (!item) return;
        if (item.action === 'go') {
            if (item.kind === 'place') pickPlace(item.result);
            else goToArea(item.entry);
        } else if (item.kind === 'place') {
            showPlaceArea(item.result);
        } else {
            pickArea(item.entry);
        }
    }, [goToArea, pickArea, pickPlace, showPlaceArea]);
    const pickRef = useRef({ pickPlace, pickItem });
    pickRef.current = { pickPlace, pickItem };

    // "Nära mig": the browser's location, reverse-geocoded (Nominatim) to its kommun, which is then picked
    // ("Visa bara bostäder i") — the same action as listvy's and the landing's Nära mig.
    const [nearMeState, setNearMeState] = useState('idle');
    const nearMeResetRef = useRef(null);
    useEffect(() => () => window.clearTimeout(nearMeResetRef.current), []);
    const kommunEntries = useMemo(() => areaEntries.filter((entry) => entry.type === 'kommun'), [areaEntries]);
    const handleNearMe = useCallback(() => {
        const fail = (reason) => {
            track('search.near_me', { result: reason });
            setNearMeState('error');
            window.clearTimeout(nearMeResetRef.current);
            nearMeResetRef.current = window.setTimeout(() => setNearMeState('idle'), 3500);
        };
        if (!navigator.geolocation) {
            fail('unsupported');
            return;
        }
        setNearMeState('locating');
        navigator.geolocation.getCurrentPosition(async ({ coords }) => {
            try {
                const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&zoom=10&accept-language=sv&lat=${coords.latitude}&lon=${coords.longitude}`;
                const response = await fetch(url);
                if (!response.ok) throw new Error(String(response.status));
                const address = (await response.json())?.address || {};
                const candidates = [address.municipality, address.city, address.town, address.county]
                    .filter(Boolean)
                    .map((name) => normalizeAreaSearch(String(name).replace(/\s+kommun$/i, '')));
                const entry = kommunEntries.find((kommun) => candidates.includes(normalizeAreaSearch(kommun.name)));
                if (!entry) {
                    fail('outside');
                    return;
                }
                track('search.near_me', { result: 'found', kommun: entry.name });
                setNearMeState('idle');
                pickArea(entry);
            } catch (error) {
                fail('error');
            }
        }, (error) => fail(error?.code === 1 ? 'denied' : 'error'), { timeout: 10000, maximumAge: 600000 });
    }, [kommunEntries, pickArea]);
    const nearMeLabel = nearMeState === 'locating'
        ? (swedish ? 'Letar…' : 'Locating…')
        : nearMeState === 'error'
            ? (swedish ? 'Hittade inte din plats' : "Couldn't find you")
            : (swedish ? 'Nära mig' : 'Near me');

    // Enter or the round button: the highlighted item; with nothing listed yet, the first place found.
    const submit = useCallback(async () => {
        const control = controlRef.current;
        if (!control) return;
        const item = itemsRef.current[activeRef.current] || itemsRef.current[0];
        if (item) {
            pickRef.current.pickItem(item);
            return;
        }
        const text = control.searchElement.input.value.trim();
        if (!text) {
            control.searchElement.input.focus();
            return;
        }
        const results = await propsRef.current.provider.search({ query: text });
        if (results?.length) pickRef.current.pickPlace(results[0]);
    }, []);

    useEffect(() => {
        const control = new GeoSearchControl({
            provider: propsRef.current.provider,
            style: 'bar',
            showMarker: false,
            autoComplete: false,
            retainZoomLevel: false,
            animateZoom: true,
            keepResult: true,
            searchLabel,
        });
        control.onSubmit = () => submit();
        map.addControl(control);
        controlRef.current = control;
        const { form: formElement, input } = control.searchElement;
        // The round green search button at the end of the field.
        const submitButton = document.createElement('button');
        submitButton.type = 'button';
        submitButton.className = 'geosearch-submit';
        submitButton.setAttribute('aria-label', submitLabel || 'Sök');
        submitButton.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>';
        submitButton.addEventListener('click', (event) => {
            event.preventDefault();
            submit();
        });
        formElement.appendChild(submitButton);

        const onInput = () => {
            setQuery(input.value);
            setActiveIndex(0);
            setOpen(true);
        };
        const onKeyDown = (event) => {
            const count = itemsRef.current.length;
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                if (!count) return;
                event.preventDefault();
                setOpen(true);
                setActiveIndex((index) => (event.key === 'ArrowDown' ? (index + 1) % count : (index - 1 + count) % count));
            } else if (event.key === 'Enter') {
                // Instead of the control's own submit (which flies to the first place).
                event.preventDefault();
                event.stopPropagation();
                submit();
            } else if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        const onFocus = () => setOpen(true);
        const onBlur = () => window.setTimeout(() => setOpen(false), 150);
        input.addEventListener('input', onInput);
        input.addEventListener('keydown', onKeyDown, true);
        input.addEventListener('focus', onFocus);
        input.addEventListener('blur', onBlur);
        // The control's × empties the field.
        const resetButton = control.resetButton;
        const onReset = () => {
            setQuery('');
            setOpen(false);
        };
        resetButton?.addEventListener('click', onReset);
        const onShowLocation = (event) => propsRef.current.onSearchResult?.(event?.location || null);
        map.on('geosearch/showlocation', onShowLocation);
        setForm(formElement);
        return () => {
            input.removeEventListener('input', onInput);
            input.removeEventListener('keydown', onKeyDown, true);
            input.removeEventListener('focus', onFocus);
            input.removeEventListener('blur', onBlur);
            resetButton?.removeEventListener('click', onReset);
            map.off('geosearch/showlocation', onShowLocation);
            submitButton.remove();
            map.removeControl(control);
            controlRef.current = null;
            setForm(null);
        };
    // The control is made once per map; labels change through the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, submit]);

    useEffect(() => {
        const control = controlRef.current;
        if (!control) return;
        control.searchElement.input.placeholder = searchLabel || '';
        const button = control.searchElement.form.querySelector('.geosearch-submit');
        if (button) button.setAttribute('aria-label', submitLabel || 'Sök');
    }, [form, searchLabel, submitLabel]);

    // Places: Nominatim after a pause in typing (it allows about a request a second).
    useEffect(() => {
        const text = query.trim();
        if (text.length < 3) {
            setPlaces({ query: text, results: [] });
            return undefined;
        }
        let cancelled = false;
        const timer = window.setTimeout(async () => {
            try {
                const results = await propsRef.current.provider.search({ query: text });
                if (!cancelled) setPlaces({ query: text, results: Array.isArray(results) ? results : [] });
            } catch (error) {
                if (!cancelled) setPlaces({ query: text, results: [] });
            }
        }, PLACE_DELAY_MS);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [query]);

    // Where the field is on screen, for the list and the chips under it.
    useLayoutEffect(() => {
        if (!form) return undefined;
        const measure = () => {
            const box = form.getBoundingClientRect();
            setRect({ left: box.left, top: box.bottom, width: box.width });
        };
        measure();
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
        observer?.observe(form);
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [form]);

    if (!rect) return null;
    const showList = open && query.trim().length >= 2 && items.length > 0;
    const goRows = items.filter((item) => item.action === 'go');
    const showRows = items.filter((item) => item.action === 'show');
    const tagFor = (entry) => {
        if (entry.type === 'kommun') return swedish ? 'Kommun' : 'Municipality';
        if (entry.type === 'lan') return swedish ? 'Län' : 'County';
        if (entry.type === 'postort') {
            const count = entry.regsoIds.length;
            // The count drops out on phones (mapStyle.css), where the tag column would squeeze the names.
            return (
                <>
                    {swedish ? 'Postort' : 'Postal town'}
                    <span className="kartvy-search-tag-count">
                        {swedish
                            ? ` · ${count} ${count === 1 ? 'område' : 'områden'}`
                            : ` · ${count} ${count === 1 ? 'area' : 'areas'}`}
                    </span>
                </>
            );
        }
        return swedish ? 'Område' : 'Area';
    };
    const placeKind = (result) => {
        const address = result?.raw?.address || {};
        if (address.road && address.house_number) return 'address';
        if (address.road && !result?.raw?.name) return 'street';
        return 'place';
    };
    const PLACE_TAGS = { address: ['Adress', 'Address'], street: ['Gata', 'Street'], place: ['Plats', 'Place'] };
    const placeTag = (result) => PLACE_TAGS[placeKind(result)][swedish ? 0 : 1];
    // An address or street is in one area, so it shows that area's score; a named place (a town, a park) may
    // cover several and has none.
    const placeScore = (result) => (placeKind(result) === 'place'
        ? null
        : placeScoreFor?.(Number(result?.y), Number(result?.x)) || null);
    const optionContent = (item) => (item.kind === 'place'
        ? (
            <>
                <span className="kartvy-search-label">{item.result.label}</span>
                <SearchScore value={placeScore(item.result)} swedish={swedish} />
                <span className="kartvy-search-tag">{placeTag(item.result)}</span>
            </>
        )
        : (
            <>
                <span className="kartvy-search-label">
                    {entryDisplayName(item.entry)}
                    {item.entry.place && <span className="kartvy-search-place"> · {item.entry.place}</span>}
                </span>
                <SearchScore value={scoreFor?.(item.entry) || null} swedish={swedish} />
                <span className="kartvy-search-tag">{tagFor(item.entry)}</span>
            </>
        ));
    const row = (item, content) => {
        const index = items.indexOf(item);
        return (
            <button
                key={item.key}
                type="button"
                className={`kartvy-search-item${index === activeIndex ? ' is-active' : ''}`}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => pickItem(item)}
            >
                {content}
            </button>
        );
    };
    const style = { left: rect.left, top: rect.top + 8, width: rect.width };

    return createPortal(
        <>
            {showList && (
                <div className="kartvy-search-menu" style={style} role="listbox">
                    <div className="kartvy-search-group">
                        <div className="kartvy-search-title is-areas">
                            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><path d="M4 5l5-2 6 2 5-2v16l-5 2-6-2-5 2z" /><path d="M9 3v16M15 5v16" /></svg>
                            {swedish ? 'Visa bara bostäder i' : 'Only show homes in'}
                        </div>
                        {showRows.map((item) => row(item, optionContent(item)))}
                    </div>
                    <div className="kartvy-search-group">
                        <div className="kartvy-search-divider" aria-hidden="true" />
                        <div className="kartvy-search-title">
                            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><path d="M12 21s-6.5-6.2-6.5-11a6.5 6.5 0 0 1 13 0c0 4.8-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2.3" /></svg>
                            {swedish ? 'Gå till' : 'Go to'}
                        </div>
                        {goRows.map((item) => row(item, optionContent(item)))}
                    </div>
                </div>
            )}
            {!showList && selectedAreas.length > 0 && (
                <div ref={chipRows.ref} className="kartvy-search-chips" style={{ left: rect.left, top: rect.top + 10, maxWidth: rect.width }}>
                    {selectedAreas.map((area, index) => (
                        <span key={area.id} className="kartvy-search-chip" data-chip="" hidden={index >= chipRows.visibleCount}>
                            {entryDisplayName(area)}
                            {area.place && <span className="kartvy-search-place"> · {area.place}</span>}
                            <button
                                type="button"
                                onClick={() => onAreaRemove?.(area.id)}
                                aria-label={swedish ? `Ta bort ${entryDisplayName(area)}` : `Remove ${entryDisplayName(area)}`}
                            >
                                ✕
                            </button>
                        </span>
                    ))}
                    <button
                        type="button"
                        className="kartvy-search-more"
                        data-chip-toggle=""
                        hidden={!chipRows.hasMore}
                        onClick={() => chipRows.setExpanded((value) => !value)}
                        aria-expanded={chipRows.expanded}
                        aria-label={chipRows.expanded
                            ? (swedish ? 'Visa färre' : 'Show fewer')
                            : (swedish ? `Visa ${chipRows.hiddenCount} till` : `Show ${chipRows.hiddenCount} more`)}
                    >
                        {chipRows.expanded ? (swedish ? 'Visa färre' : 'Show fewer') : `+${chipRows.hiddenCount}`}
                    </button>
                    <button type="button" className="kartvy-search-clear" onClick={() => onAreasClear?.()}>
                        {swedish ? 'Rensa' : 'Clear'}
                    </button>
                </div>
            )}
            {!showList && selectedAreas.length === 0 && (
                <div className="kartvy-search-nearme" style={{ left: rect.left, top: rect.top + 10 }}>
                    <button
                        type="button"
                        className={`kartvy-search-near-me${nearMeState === 'error' ? ' is-error' : ''}`}
                        onClick={handleNearMe}
                        disabled={nearMeState === 'locating'}
                    >
                        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><path d="M12 21s-6.5-6.2-6.5-11a6.5 6.5 0 0 1 13 0c0 4.8-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2.3" /></svg>
                        {nearMeLabel}
                    </button>
                </div>
            )}
        </>,
        document.body,
    );
};

export default SearchBox;
